import { read, utils } from 'xlsx';

export type CanonicalUploadColumn =
  | 'communication number'
  | 'pricelist id'
  | 'calyear'
  | 'local rrp'
  | 'local rm (%)'
  | 'local lp'
  | 'comment';

const COLUMN_ALIASES: Record<CanonicalUploadColumn, string[]> = {
  'communication number': [
    'communication number',
    'product id',
    'product_id',
    'productid',
  ],
  'pricelist id': [
    'pricelist id',
    'price list id',
    'price unit id',
    'price_unit_id',
    'priceunitid',
    'pricelistid',
  ],
  calyear: ['calyear', 'cal year', 'year'],
  'local rrp': ['local rrp', 'rrp'],
  'local rm (%)': ['local rm', 'local rm%', 'local rm %', 'rm', 'rm%'],
  'local lp': ['local lp', 'lp'],
  comment: ['comment'],
};

export const REQUIRED_UPLOAD_COLUMNS: CanonicalUploadColumn[] = [
  'communication number',
  'pricelist id',
  'calyear',
  'local rrp',
  'local rm (%)',
  'local lp',
];

export const TEMPLATE_HEADERS: string[] = [
  'Communication Number',
  'Price List ID',
  'Year',
  'Local RRP',
  'Local RM (%)',
  'Local LP',
  'Comment',
];

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

export type UploadedPriceRow = {
  productId: number;
  priceUnitId: number;
  rrp: number | null;
  lp: number | null;
  rm: number | null;
  comment: string | null;
};

export type UploadValidationError =
  | { type: 'file_too_large'; maxSizeMb: number }
  | { type: 'missing_columns'; columns: string[] }
  | { type: 'empty_file' }
  | { type: 'no_valid_rows' };

export type UploadParseWarning =
  | { type: 'invalid_rows'; rowNumbers: number[] }
  | { type: 'mismatched_year'; rowNumbers: number[]; expectedYear: number }
  | { type: 'duplicate_rows'; count: number }
  | { type: 'lp_below_threshold'; rowNumbers: number[] };

export type UploadedPriceFileParseResult = {
  rows: UploadedPriceRow[];
  errors: UploadValidationError[];
  warnings: UploadParseWarning[];
};

const normalizeHeader = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9%]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const findColumnIndex = (
  normalizedHeaders: string[],
  canonicalColumn: CanonicalUploadColumn,
): number => {
  const aliases = COLUMN_ALIASES[canonicalColumn].map(normalizeHeader);
  for (let i = 0; i < normalizedHeaders.length; i += 1) {
    const header = normalizedHeaders[i];
    if (header && aliases.includes(header)) {
      return i;
    }
  }
  return -1;
};

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = String(value ?? '')
    .trim()
    .replace(/\s+/g, '')
    .replace(',', '.');

  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const LP_MIN_THRESHOLD = 0.01;
export const COMMENT_MAX_LENGTH = 100;

const parseRmPercent = (value: unknown): number | null => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }
    return Math.abs(value) < 1 ? value : value / 100;
  }

  const raw = String(value ?? '').trim();
  if (!raw) {
    return null;
  }

  const hasPercentSign = raw.endsWith('%');
  const parsed = parseNumber(hasPercentSign ? raw.slice(0, -1) : raw);
  if (parsed === null) {
    return null;
  }

  return hasPercentSign || Math.abs(parsed) >= 1 ? parsed / 100 : parsed;
};

const isEmptyRow = (row: unknown[]): boolean =>
  row.length === 0 ||
  row.every((cell) => cell === null || String(cell).trim() === '');

export const parseUploadedPriceFile = async (
  file: File,
  planningYear: number,
): Promise<UploadedPriceFileParseResult> => {
  const errors: UploadValidationError[] = [];
  const warnings: UploadParseWarning[] = [];

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      rows: [],
      errors: [{ type: 'file_too_large', maxSizeMb: 10 }],
      warnings: [],
    };
  }

  const arrayBuffer = await file.arrayBuffer();
  const workbook = read(arrayBuffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName || !workbook.Sheets[firstSheetName]) {
    return { rows: [], errors: [{ type: 'empty_file' }], warnings: [] };
  }

  const sheet = workbook.Sheets[firstSheetName];
  const rawRows = utils.sheet_to_json(sheet, {
    header: 1,
    defval: null,
  }) as unknown[][];

  if (rawRows.length === 0) {
    return { rows: [], errors: [{ type: 'empty_file' }], warnings: [] };
  }

  const headerRow = rawRows[0];
  const normalizedHeaders = (headerRow ?? []).map(normalizeHeader);
  const missingColumns = REQUIRED_UPLOAD_COLUMNS.filter(
    (column) => findColumnIndex(normalizedHeaders, column) < 0,
  );

  if (missingColumns.length > 0) {
    return {
      rows: [],
      errors: [{ type: 'missing_columns', columns: missingColumns }],
      warnings: [],
    };
  }

  const colIdx = {
    productId: findColumnIndex(normalizedHeaders, 'communication number'),
    priceUnitId: findColumnIndex(normalizedHeaders, 'pricelist id'),
    calYear: findColumnIndex(normalizedHeaders, 'calyear'),
    rrp: findColumnIndex(normalizedHeaders, 'local rrp'),
    lp: findColumnIndex(normalizedHeaders, 'local lp'),
    rm: findColumnIndex(normalizedHeaders, 'local rm (%)'),
    comment: findColumnIndex(normalizedHeaders, 'comment'),
  };

  const uploadedRowsByKey = new Map<string, UploadedPriceRow>();
  const invalidRowNumbers: number[] = [];
  const mismatchedYearRowNumbers: number[] = [];
  const lpBelowThresholdRows: number[] = [];
  let duplicateCount = 0;

  for (let i = 1; i < rawRows.length; i += 1) {
    const row = rawRows[i];
    if (!row || isEmptyRow(row)) {
      continue;
    }

    const productId = parseNumber(row[colIdx.productId]);
    const priceUnitId = parseNumber(row[colIdx.priceUnitId]);
    const calYear = parseNumber(row[colIdx.calYear]);
    const rrp = parseNumber(row[colIdx.rrp]);
    const lp = parseNumber(row[colIdx.lp]);
    const rm = parseRmPercent(row[colIdx.rm]);

    if (productId === null || priceUnitId === null || calYear === null) {
      invalidRowNumbers.push(i + 1);
      continue;
    }

    if (calYear !== planningYear) {
      mismatchedYearRowNumbers.push(i + 1);
      continue;
    }

    if (lp !== null && lp < LP_MIN_THRESHOLD) {
      lpBelowThresholdRows.push(i + 1);
      continue;
    }

    const comment =
      colIdx.comment >= 0
        ? String(row[colIdx.comment] ?? '')
            .trim()
            .slice(0, COMMENT_MAX_LENGTH)
            .replace(/[\uD800-\uDBFF]$/, '') || null // strip lone high surrogate from sliced emoji
        : null;

    const key = `${productId}::${priceUnitId}`;
    if (uploadedRowsByKey.has(key)) {
      duplicateCount += 1;
    }
    uploadedRowsByKey.set(key, {
      productId,
      priceUnitId,
      rrp,
      lp,
      rm,
      comment,
    });
  }

  if (invalidRowNumbers.length > 0) {
    warnings.push({ type: 'invalid_rows', rowNumbers: invalidRowNumbers });
  }

  if (mismatchedYearRowNumbers.length > 0) {
    warnings.push({
      type: 'mismatched_year',
      rowNumbers: mismatchedYearRowNumbers,
      expectedYear: planningYear,
    });
  }

  if (duplicateCount > 0) {
    warnings.push({ type: 'duplicate_rows', count: duplicateCount });
  }

  if (lpBelowThresholdRows.length > 0) {
    warnings.push({
      type: 'lp_below_threshold',
      rowNumbers: lpBelowThresholdRows,
    });
  }

  const rows = Array.from(uploadedRowsByKey.values());

  if (rows.length === 0 && errors.length === 0) {
    errors.push({ type: 'no_valid_rows' });
  }

  return { rows, errors, warnings };
};
